var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as _ from 'lodash';
import cx from 'classnames';
import * as React from 'react';
import { mergeStyles } from './mergeThemes';
var CREATE_SHORTHAND_DEFAULT_OPTIONS = {
    defaultProps: {},
    overrideProps: {},
    generateKey: true,
    render: function (Component, props) { return React.createElement(Component, props); },
};
// It's only necessary to map props that don't use 'children' as value ('children' is the default)
var mappedProps = {
    iframe: 'src',
    img: 'src',
    input: 'type',
};
// ============================================================
// Factories
// ============================================================
/** A more robust React.createElement. It can create elements from primitive values. */
export function createShorthand(Component, mappedProp, valueOrRenderCallback, options) {
    if (options === void 0) { options = CREATE_SHORTHAND_DEFAULT_OPTIONS; }
    var valIsRenderFunction = typeof valueOrRenderCallback === 'function' && !React.isValidElement(valueOrRenderCallback);
    if (valIsRenderFunction) {
        return createShorthandFromRenderCallback(Component, mappedProp, valueOrRenderCallback, options);
    }
    return createShorthandFromValue(Component, mappedProp, valueOrRenderCallback, options);
}
// ============================================================
// Factory Creators
// ============================================================
/**
 * @param {React.ReactType} Component A ReactClass or string
 * @param {string} mappedProp A function that maps a primitive value to the Component props
 * @returns {function} A shorthand factory function waiting for `val` and `defaultProps`.
 */
export function createShorthandFactory(Component, mappedProp) {
    if (typeof Component !== 'function' && typeof Component !== 'string') {
        throw new Error('createShorthandFactory() Component must be a string or function.');
    }
    return function (val, options) { return createShorthand(Component, mappedProp, val, options); };
}
// ============================================================
// Private Utils
// ============================================================
function createShorthandFromValue(Component, mappedProp, value, options) {
    if (options === void 0) { options = CREATE_SHORTHAND_DEFAULT_OPTIONS; }
    if (typeof Component !== 'function' && typeof Component !== 'string') {
        throw new Error('createShorthand() Component must be a string or function.');
    }
    // short circuit noop values
    var valIsNoop = _.isNil(value) || typeof value === 'boolean';
    if (valIsNoop && !options.render)
        return null;
    var valIsPrimitive = typeof value === 'string' || typeof value === 'number';
    var valIsPropsObject = _.isPlainObject(value);
    var valIsReactElement = React.isValidElement(value);
    // unhandled type warning
    if (process.env.NODE_ENV !== 'production' &&
        !valIsPrimitive &&
        !valIsPropsObject &&
        !valIsReactElement &&
        !valIsNoop) {
        console.error([
            'Shorthand value must be a string|number|object|ReactElements.',
            ' Use null|undefined|boolean for none.',
            " Received: " + value,
        ].join(''));
    }
    // return value 'as is' if it a ReactElement
    if (valIsReactElement) {
        return value;
    }
    // ----------------------------------------
    // Build up props
    // ----------------------------------------
    var _a = options.defaultProps, defaultProps = _a === void 0 ? {} : _a;
    // User's props
    var usersProps = valIsPropsObject ? value : {};
    // Override props
    var overrideProps = options.overrideProps;
    overrideProps =
        typeof overrideProps === 'function'
            ? overrideProps(__assign({}, defaultProps, usersProps))
            : overrideProps || {};
    // Merge props
    var props = __assign({}, defaultProps, usersProps, overrideProps);
    // Map prop for primitive value
    if (valIsPrimitive) {
        props[mappedProps[overrideProps.as || defaultProps.as] || mappedProp || 'children'] = value;
    }
    // Merge className
    if (defaultProps.className || overrideProps.className || usersProps.className) {
        var mergedClassesNames = cx(defaultProps.className, overrideProps.className, usersProps.className);
        props.className = _.uniq(mergedClassesNames.split(' ')).join(' ');
    }
    // Merge style
    if (defaultProps.style || overrideProps.style || usersProps.style) {
        props.style = __assign({}, defaultProps.style, usersProps.style, overrideProps.style);
    }
    // Merge styles
    if (defaultProps.styles || overrideProps.styles || usersProps.styles) {
        props.styles = mergeStyles(defaultProps.styles, usersProps.styles, overrideProps.styles);
    }
    // ----------------------------------------
    // Get key
    // ----------------------------------------
    var _b = options.generateKey, generateKey = _b === void 0 ? true : _b;
    // Use key or generate key
    if (generateKey && _.isNil(props.key) && valIsPrimitive) {
        // use string/number shorthand values as the key
        props.key = value;
    }
    // ----------------------------------------
    // Create Element
    // ----------------------------------------
    var render = options.render;
    if (render) {
        return render(Component, props);
    }
    // Create ReactElements from built up props
    if (valIsPrimitive || valIsPropsObject)
        return React.createElement(Component, props);
    return null;
}
function createShorthandFromRenderCallback(Component, mappedProp, renderCallback, options) {
    if (options === void 0) { options = CREATE_SHORTHAND_DEFAULT_OPTIONS; }
    var render = function (shorthandValue, renderTree) {
        return createShorthandFromValue(Component, mappedProp, shorthandValue, __assign({}, options, (renderTree && { render: renderTree })));
    };
    return renderCallback(render);
}
